
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { hasRole } from '@/lib/auth';
import { toast as sonnerToast } from "sonner";
import { Pencil } from "lucide-react";

type TranslationEditContextType = {
  isEditMode: boolean;
  toggleEditMode: () => void;
  editingKey: string | null;
  setEditingKey: (key: string | null) => void;
  canEditTranslations: boolean;
};

const TranslationEditContext = createContext<TranslationEditContextType | undefined>(undefined);

export function TranslationEditProvider({ children }: { children: ReactNode }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [canEditTranslations, setCanEditTranslations] = useState(false);
  const { toast } = useToast();
  const { user, isLoading } = useAuth();

  useEffect(() => {
    // Only check for admin role if there's a user and they're not loading
    if (!isLoading && user) {
      const checkAdminRole = async () => {
        try {
          const isAdmin = await hasRole(user.id, 'ADMIN');
          setCanEditTranslations(isAdmin);
        } catch (error) {
          console.error("Error checking admin role:", error);
          setCanEditTranslations(false);
        }
      };
      checkAdminRole();
    } else {
      setCanEditTranslations(false);
    }
  }, [user, isLoading]);

  // Reset edit mode when user logs out
  useEffect(() => {
    if (!user && isEditMode) {
      setIsEditMode(false);
    }
  }, [user, isEditMode]);

  // Apply styles for edit mode
  useEffect(() => {
    if (isEditMode) {
      document.body.classList.add('translation-edit-mode');
      
      const style = document.createElement('style');
      style.id = 'translation-edit-styles';
      style.textContent = `
        .translation-editable {
          position: relative;
          border: 1px dashed transparent;
          transition: all 0.2s ease;
        }
        .translation-editable:hover {
          border-color: #3b82f6;
          background-color: rgba(59, 130, 246, 0.1);
        }
        input.translation-editable, 
        button.translation-editable {
          border: 1px dashed #3b82f6;
          background-color: rgba(59, 130, 246, 0.05);
        }
        input.translation-editable:hover, 
        button.translation-editable:hover {
          background-color: rgba(59, 130, 246, 0.1);
        }
      `;
      document.head.appendChild(style);
    } else {
      document.body.classList.remove('translation-edit-mode');
      setEditingKey(null);
      
      const style = document.getElementById('translation-edit-styles');
      if (style) {
        style.remove();
      }
    }
    
    return () => {
      document.body.classList.remove('translation-edit-mode');
      const style = document.getElementById('translation-edit-styles');
      if (style) {
        style.remove();
      }
    };
  }, [isEditMode]);

  const toggleEditMode = () => {
    if (!canEditTranslations) {
      toast({
        title: "Permission denied",
        description: "You need admin privileges to edit translations",
        variant: "destructive",
      });
      return;
    }
    
    const newEditMode = !isEditMode;
    setIsEditMode(newEditMode);
    
    if (newEditMode) {
      sonnerToast.success(
        "Translation Edit Mode Enabled", 
        {
          description: "Click on any highlighted text to edit its translation",
          icon: <Pencil className="h-5 w-5 text-green-500" />,
          duration: 5000
        }
      );
    } else {
      sonnerToast.info(
        "Translation Edit Mode Disabled",
        {
          description: "Translation editing is now disabled",
          duration: 3000
        }
      );
    }
  };

  return (
    <TranslationEditContext.Provider value={{
      isEditMode,
      toggleEditMode,
      editingKey,
      setEditingKey,
      canEditTranslations
    }}>
      {children}
    </TranslationEditContext.Provider>
  );
}

export function useTranslationEdit() {
  const context = useContext(TranslationEditContext);
  
  if (context === undefined) {
    throw new Error('useTranslationEdit must be used within a TranslationEditProvider');
  }
  
  return context;
}
